import { datadogRum } from '@datadog/browser-rum'
import { useEffect } from 'react'
import { generateDataDogContext } from '../utils/dataDog'

export interface SessionRouteConfigGuardArgs {
  /**
   * The answer `deriveSessionRequirement` gave for the path in hand. `false`
   * is the only value this hook acts on, and — see the docstring below — it
   * has exactly one producer.
   */
  requiresSession: boolean
  /**
   * The path the requirement was derived from: `useLocation().pathname`, with
   * the router `basename` already stripped. Deliberately *not*
   * `window.location.pathname`, which under the ephemeral/host deployment
   * carries a commit SHA the authored routes know nothing about. The context
   * `generateDataDogContext` builds carries the raw one under `pathname`, so
   * the two differ there on purpose and both are worth having.
   */
  pathname: string
  /**
   * The storefront the path names, read through `getUrlParams` so the
   * ephemeral commit SHA and the language prefix are stripped. Passed
   * explicitly for the same reason `pathname` is: `generateDataDogContext`
   * derives its own `storefrontName` from
   * `window.location.pathname.split('/').filter(Boolean)[0]`, which on
   * `/a1b2c3d/acme/about` is the **SHA** and on `/es/acme/about` is the
   * **language**. Settling AC 2 means opening that storefront's SCS config, so
   * a report that names the wrong storefront cannot be acted on at all.
   * Measured on all three shapes, not inferred.
   */
  slug: string
  /**
   * What `getUrlParams` reads out of the second path segment — the session id
   * the pre-COM-738 code would have dispatched and prefetched against. It is
   * the single most diagnostic field in the report; see below.
   *
   * ⚠️ **`string | undefined`, and the `undefined` is the flagship case rather
   * than a corner.** `getUrlParams` types this `string` optimistically (see
   * the `SessionRequirement` docstring in `useSessionGuard.ts`, which records
   * the same optimism), but a session-less *entry* route — `/acme`, the shape
   * COM-738 exists to make loadable — has no second segment at all. Every
   * report on that shape carries `undefined` here. Typed honestly so the
   * `?? null` below reads as the live branch it is.
   */
  positionalSessionId: string | undefined
  /**
   * The authored `route` string of the match the requirement came from, e.g.
   * `/:slug/about`. Passed in rather than re-derived so the report cannot
   * describe a different match from the one the decision was made on.
   *
   * Optional only because `matchAuthoredRoute` returns `undefined` when
   * nothing matched — `route.path` itself is a plain `string` since COM-973's
   * review. A no-match is a case this hook never reports on, since an
   * unmatched path takes the `requiresSession: true` fallback, so inside the
   * report this is present in every reachable state.
   */
  matchedRoutePath?: string
}

/**
 * Reports a matched route that declares no `:sessionId` (COM-973).
 *
 * ## Why this exists
 *
 * COM-738 made the session requirement per route, and the
 * `requiresSession: false` branch in `containers/Config.tsx` is that feature's
 * entire suppression surface. It cannot tell "the author meant this" from "the
 * author slipped": an SCS author who writes `/:slug/orders` instead of
 * `/:slug/:sessionId/orders`, or who renames the param to `:session`, gets no
 * `setSessionId`, no catalog prefetch, no `INITIAL_DATA` loader, no
 * `manageErrors` and — before this hook — no record anywhere. The container
 * mounts over an empty store and renders blank bindings.
 *
 * **Before COM-738 the same slip was not silent — but do not describe what it
 * did as a `NonProvidedParams` error with a Datadog error behind it. It was
 * neither of those things.** That reading is in COM-973's own ticket text, and
 * both halves are wrong:
 *
 * - **`NonProvidedParams` raises no RUM event.** `manageErrors` is a plain
 *   reducer in `redux/slices/config/config.ts`; every `datadogRum` call in that
 *   file sits in `fetchConfig`'s thunk. The two were never a pair.
 * - **It would not have fired for *this* slip.** The pre-COM-738 branch was
 *   `if (!slugFromUrl || !sessionId)` over the positional read, so on
 *   `/acme/orders` the session id was the truthy string `'orders'` and the
 *   check passed. It fired only on a path carrying no second segment at all —
 *   `/acme` — which is the deliberate entry-route shape, not the slip. The
 *   docstring used to conflate the two cases, which this hook exists to
 *   separate.
 *
 * What the slip actually did was put the literal segment into the store as a
 * session id and fire the prefetch against it. The loud failure, *where it
 * came*, came from `useInitialCatalogData`'s `onError` —
 * `ErrorMessageTranslation.InitialData` plus its own RUM error — and that is
 * conditional on the storefront API rejecting a bogus `sessionUid`, which is a
 * claim about the other side of a boundary that this repo cannot check. Stated
 * as a dependency on purpose; do not harden it into a fact.
 *
 * The conclusion is unchanged either way, which is why the correction is to
 * the mechanism alone: what the slip leaves behind today is no usable signal,
 * and this hook restores one — a signal and only a signal. The behaviour
 * COM-738 introduced is correct for a storefront that meant it, so the fix is
 * a report rather than a throw. Same reasoning, and the same shape, as
 * `hooks/orders/useOrderConfirmationConfigGuards.ts` — see its docstring on
 * reporting over throwing — and as the `hidePrices` and `packages.sort`
 * reports in `redux/slices/config/config.ts`'s `fetchConfig`. Taking a page
 * down over an authored route that works would be the larger outage.
 *
 * ## Telling the two apart (AC 2)
 *
 * The reader needs every diagnostic facet below; read the authoritative list
 * off `extraInfo` in the hook body rather than counting them here.
 *
 * - **the pathname** — which page this was, so the report can be matched
 *   against the storefront's own config;
 * - **the slug** — *which storefront* to open that config for. Taken from
 *   `getUrlParams`, not from the context's own `storefrontName`, which is the
 *   naive first path segment and so reads the commit SHA on an ephemeral
 *   deployment and the language on a prefixed one;
 * - **the authored route** the path matched — this is what makes a *renamed
 *   param* legible. `/:slug/:session` in this field is a slip that no amount
 *   of staring at the URL would reveal, because the URL of a `:session` typo
 *   is indistinguishable from a correct one;
 * - **the positional session id** the pre-guard code would have used — a word
 *   like `"about"` reads as a page a storefront meant to author, an opaque
 *   token reads as a session id that has just been dropped on the floor.
 *
 * ## What it must stay silent about (AC 3)
 *
 * A session-bearing route, the pre-config renders, a fall-through to
 * `NotFound`, and a splat match. **All four are inherited rather than
 * re-derived here**: every one of them resolves to `requiresSession: true`, the
 * conservative fallback COM-738 chose, so `if (requiresSession) return` covers
 * them without this file knowing they exist. That inheritance is safe because
 * `deriveSessionRequirement` has exactly *one* `return { requiresSession:
 * false }` — the token-absent branch on a concrete match — but it is an
 * inheritance, not an independent guarantee: widen that fallback and this hook
 * starts reporting on every `NotFound` in the estate, from a diff that does not
 * mention Datadog. `hooks/tests/sessionRouteConfigGuard.test.tsx` pins each of
 * the four at this level for that reason, rather than trusting the derivation's
 * own tests to stand in for them.
 *
 * ## Once per session-less *route*, and not once per render (AC 1, AC 4)
 *
 * `Config` reads `useLocation()` as of COM-738, so it re-renders on every
 * navigation — including navigation *within* one session-less route, where
 * nothing about the misconfiguration has changed. The effect therefore keys on
 * `[requiresSession, matchedRoutePath]`, which is the smallest pair that tells
 * those two motions apart. Both halves are load-bearing and each was wrong on
 * its own at some point in this ticket:
 *
 * - **without `matchedRoutePath` the hook is close to useless**, and this is
 *   the bug COM-973's review caught. `requiresSession === false` is a property
 *   of a *route*, and a storefront may author several; keyed on the flag alone,
 *   the effect fires on the first session-less route of a visit and never
 *   again. A storefront with one deliberate landing page and one slipped route
 *   reports **the landing page** and stays silent about the slip — an operator
 *   reading that one error mutes the monitor, which is the exact inversion of
 *   what this hook is for. Measured, with a two-route fixture.
 * - **with `pathname` in the array it re-reports on every step** a shopper
 *   takes through a session-less area, because two paths under one authored
 *   route are one misconfiguration, not two. `matchedRoutePath` is a stable
 *   string across `/acme/cat/drinks` → `/acme/cat/snacks`; `pathname` is not.
 *
 * The other values are read out of the closure at the moment the pair changes,
 * which is exactly the render that describes the change.
 *
 * Keying on the route rather than latching once per mount is deliberate:
 * navigating session-less → session-bearing → back is two distinct
 * misconfigured page loads and reports twice. A `useRef` latch would collapse
 * them into one and hide the second surface.
 *
 * Read that as "once per contiguous stay on a session-less route", not "once
 * per route per visit": a shopper going `/acme/about` → `/acme/cat/drinks` →
 * `/acme/about` reports three times, because the pair changes each time. That
 * is the same duplication the unlatched stance accepts everywhere else in this
 * file, and it is deliberate — a monitor grouping on `matchedRoute` collapses
 * it, whereas a report that went missing cannot be recovered by grouping.
 * Measured, in the ANTI-VACUITY test's fourth navigation, rather than left as
 * a claim here.
 *
 * **"Exactly one" holds in development as well as in production, and do not
 * re-justify a caveat here by `<StrictMode>` — an earlier revision of this
 * comment did, and it was wrong.** React 18 double-invokes effects only on a
 * **concurrent** root, and cinnamon has none: `index.tsx` mounts through the
 * legacy `ReactDOM.render`, there is no `createRoot` in either package, and
 * the host shell is on `react-dom@17.0.2`. The `<StrictMode>` wrapper in
 * `index.tsx` is real but inert for effects. The suite *does* see two reports,
 * because `@testing-library/react` v13+ renders through `createRoot` — which
 * makes the tests a stricter environment than the app currently is, not a
 * mirror of it. The same retraction, with the same three reasons, is at
 * `hooks/orders/useOrderConfirmation.ts:239-247`; read it there rather than
 * re-deriving this. The caveat becomes live the day `index.tsx` migrates to
 * `createRoot`, and not before.
 *
 * Left unlatched anyway: on a signal that is otherwise silent, reporting twice
 * is the right direction to fail in, and a latch is a new way for the report
 * to go missing altogether. `useOrderConfirmationConfigGuards` takes the same
 * stance — for its own reason, which is that *two consumers mount it at once*
 * (see its docstring), not anything to do with StrictMode. The suite's
 * double-invocation is pinned as a characterisation in
 * `hooks/tests/sessionRouteConfigGuard.test.tsx`.
 *
 * @internal
 */
export const useSessionRouteConfigGuard = ({
  requiresSession,
  pathname,
  slug,
  positionalSessionId,
  matchedRoutePath,
}: SessionRouteConfigGuardArgs) => {
  useEffect(() => {
    if (requiresSession) return

    const context = generateDataDogContext({
      title: 'route declares no :sessionId',
      extraInfo: {
        function: 'useSessionGuard',
        // The router's pathname, not `window.location`'s. The context's own
        // `pathname` field is the raw one; keeping both is what makes an
        // ephemeral deployment's report readable.
        routerPathname: pathname,
        // The SHA- and language-aware storefront name, for the same reason.
        // The context's own `storefrontName` is the naive first segment and is
        // wrong on both of those deployments — see the arg's docstring.
        slug,
        // ⚠️ **This `?? null`, and the matching one in the message below, are
        // defensive rather than live** — labelled per COM-736, because an
        // unlabelled fallback reads as a measured population. Reporting at all
        // requires a match, and since COM-973's review `route.path` is typed
        // `string`, so the `null` arm is unreachable in both places. The
        // coverage run agrees: both sites report the taken arm only, with the
        // `null` arm at zero.
        //
        // **The two `?? null`s below it are the opposite and must not be
        // deleted with these.** `positionalSessionId` is genuinely
        // `string | undefined` and `undefined` on the flagship `/acme` shape,
        // and `JSON.stringify(undefined)` returns the *value* `undefined`
        // rather than a string — so dropping that fallback does not render
        // "undefined", it drops the facet out of the Datadog payload
        // altogether. Pinned by
        // `carries "null" in the facet, not a dropped field, on an entry route`.
        matchedRoute: matchedRoutePath ?? null,
        // Stringified for the reason `useOrderConfirmationConfigGuards`
        // stringifies `required`: it is the difference between an absent
        // segment and an empty one in the Datadog UI.
        positionalSessionId: JSON.stringify(positionalSessionId ?? null),
      },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(
      new Error(
        `session-less route: ${JSON.stringify(
          pathname,
        )} matched the authored route ${JSON.stringify(
          matchedRoutePath ?? null,
        )}, which names no :sessionId, so this page stores no session id and fires no catalog prefetch; the pre-guard code would have used ${JSON.stringify(
          positionalSessionId ?? null,
        )} as the session id`,
      ),
      context,
    )
    // Dependencies are the whole design of this hook, not an oversight — see
    // "once per session-less route" above. `requiresSession` and
    // `matchedRoutePath` are the minimum that distinguishes two session-less
    // routes from two paths under one: `requiresSession` alone silences every
    // session-less route after the first, and adding `pathname` re-reports on
    // every step a shopper takes within one. `positionalSessionId` is read
    // deliberately out of the render in which the key changed.
    //
    // ⚠️ **`slug` is in the key for identity, and is defensive rather than
    // live** — labelled per COM-736 so it does not read as a measured
    // population. The report identifies a *(route, storefront)* pair: two
    // storefronts can author the same template, and `matchedRoutePath` is the
    // authored string (`/:slug/about`), which is identical across them. So
    // keying without `slug` would dedup a second storefront's report against
    // the first's.
    //
    // It cannot fire today, and no test here can kill it. Changing `slug`
    // without remounting needs a client-side navigation across storefronts,
    // and cinnamon builds none: every authored path interpolates
    // `{{params.slug}}` and `useOrderActions`' two `navigate` calls read the
    // current slug, so every SPA history entry carries the same one. A typed
    // URL or an external link changes it, but both are document loads that
    // remount this hook. (`Config`'s own `slug !== slugFromUrl` check is the
    // boot guard — `storeName` starts `undefined` — not support for switching
    // storefronts in place; a PR review read it as the latter.) Raised by the
    // release-assistant review of #754.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiresSession, matchedRoutePath, slug])
}

export default useSessionRouteConfigGuard
